import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { GetProductions, GetProductionById, CreateProduction } from "../Api";
import {
  DEFAULT_STALE_TIME,
  LOOKUP_STALE_TIME,
  ServiceQueryOptions,
  unwrapApiResponse,
} from "./query_helpers";

export const productionQueryKeys = {
  productions: (params?: Parameters<typeof GetProductions>[0]) =>
    ["productions", params] as const,
  productionById: (params?: Parameters<typeof GetProductionById>[0]) =>
    ["productionById", params] as const,
};

export const invalidateProductionQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["productions"] });
  queryClient.invalidateQueries({ queryKey: ["productionById"] });
};

export const useProductionsQuery = (
  params: Parameters<typeof GetProductions>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: productionQueryKeys.productions(params),
    queryFn: async () => unwrapApiResponse(await GetProductions(params)),
  });
};

export const useProductionsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetProductions>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: productionQueryKeys.productions(params),
        queryFn: async () => unwrapApiResponse(await GetProductions(params)),
      }),
  });
};

export const useProductionByIdQuery = (
  params: Parameters<typeof GetProductionById>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: productionQueryKeys.productionById(params),
    queryFn: async () => unwrapApiResponse(await GetProductionById(params)),
  });
};

export const useProductionByIdRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetProductionById>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: productionQueryKeys.productionById(params),
        queryFn: async () => unwrapApiResponse(await GetProductionById(params)),
      }),
  });
};

export const useCreateProductionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateProduction>[0]) =>
      CreateProduction(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateProductionQueries(queryClient);
      }
    },
  });
};

export const useProductionService = (config?: {
  fetchProductionsParams?: Parameters<typeof GetProductions>[0];
  fetchProductionsEnabled?: boolean;
  fetchProductionByIdParams?: Parameters<typeof GetProductionById>[0];
  fetchProductionByIdEnabled?: boolean;
}) => {
  const productionsQuery = useProductionsQuery(config?.fetchProductionsParams, {
    enabled:
      config?.fetchProductionsEnabled !== false &&
      config?.fetchProductionsParams !== undefined &&
      config?.fetchProductionsParams !== null,
  });

  const productionByIdQuery = useProductionByIdQuery(
    config?.fetchProductionByIdParams,
    {
      enabled:
        config?.fetchProductionByIdEnabled !== false &&
        config?.fetchProductionByIdParams !== undefined &&
        config?.fetchProductionByIdParams !== null,
    },
  );

  const createProductionMutation = useCreateProductionMutation();

  return {
    productionsQuery,
    productionByIdQuery,
    createProductionMutation,
  };
};
